import { Component, computed, inject, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Observable, of } from 'rxjs';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { KeywordInputComponent } from '../keyword-input/keyword-input.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { LutService } from '../../../../../services/lut.service';
import { CompanyMeetingService } from '../../../../../services/companies/company-meeting.service';
import {
  CompanyMeetingResponseDto,
  CompanyMeetingDetailDto,
  CompanyVoteResultDto,
  CompanyMeetingUpsertRequest,
  CompanyMeetingDetailUpsertDto,
  CompanyVoteResultUpsertDto
} from '../../../../../models/api';
import { VoteResultsOption } from '../../../../../models/voteResultsOption';
import { VoteResultsCategory } from '../../../../../models/voteResultsCategory';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { selectTabYear, selectDefaultYear } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear } from '../../../../../store/actions/company-form.actions';
import { NumberFormatDirective } from '../../../../../directives/number-format.directive';
import { MatTableDataSource } from '@angular/material/table';
import { EntityType } from '../../../../../models/enums/entityType.enum';
import { AddProposalNameDialogComponent, AddProposalNameDialogResult } from './add-proposal-name-dialog/add-proposal-name-dialog.component';
import { ElectionDialogComponent, ElectionDialogResult, ElectionCandidate } from './election-dialog/election-dialog.component';
import { CompanyKeywordService } from '../../../../../services/companies/company-keyword.service';
import { CompanyKeywordDto, CompanyKeywordUpdateRequest } from '../../../../../models/api/companyKeywordDto';
import { KeywordType } from '../../../../../models/enums/keywordType.enum';
import { buildKeywordRequests } from '../../../../../utilities/keyword-helpers';
import { saveTabThenKeywords } from '../../../../../utilities/tab-save';
import { serverErrorMessage } from '../../../../../utilities/api-error';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-voting-results-tab',
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    KeywordInputComponent,
    NumberFormatDirective,
    ReadOnlyTabDirective,
  ],
  templateUrl: './voting-results-tab.component.html',
  styleUrl: './voting-results-tab.component.scss'
})
export class VotingResultsTabComponent implements OnInit, OnChanges, HasUnsavedChanges {
  @Input() keywords: CompanyKeywordDto[] = [];
  @Output() keywordsUpdated = new EventEmitter<CompanyKeywordDto[]>();

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private lutService = inject(LutService);
  private meetingService = inject(CompanyMeetingService);
  private companyKeywordService = inject(CompanyKeywordService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private toast = inject(HotToastService);
  private spinnerService = inject(NgxSpinnerService);
  private dialog = inject(MatDialog);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.VotingResults));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  showKeywords = true;
  meetingId: string | null = null;

  // Lookup data
  allOptions: VoteResultsOption[] = [];
  boardVoteRecOptions: VoteResultsOption[] = [];      // type 1
  decidedByOptions: VoteResultsOption[] = [];         // type 2
  voteRequirementOptions: VoteResultsOption[] = [];   // type 6
  voteResultOptions: VoteResultsOption[] = [];        // type 7
  categories: VoteResultsCategory[] = [];
  parentCategories: VoteResultsCategory[] = [];
  subcategories: VoteResultsCategory[] = [];

  // Data sources for tables
  issueDataSource = new MatTableDataSource<CompanyVoteResultDto>([]);
  sayOnPayDataSource = new MatTableDataSource<CompanyVoteResultDto>([]);

  issueDisplayedColumns: string[] = [
    'select', 'voteType', 'voteRank', 'issue', 'category', 'subcategory',
    'requirement', 'shareholderProposal', 'proposalName',
    'boardVoteRec', 'forVotes', 'forVotesPct', 'againstVotes', 'againstVotesPct',
    'abstainVotes', 'voteWithheld', 'brokerNonVote', 'totalVotes',
    'voteResult', 'decidedBy', 'delete'
  ];

  sayOnPayDisplayedColumns: string[] = [
    'select', 'voteType', 'voteRank', 'issue', 'category', 'subcategory',
    'requirement', 'shareholderProposal', 'proposalName',
    'boardVoteRec',
    'firstYearVotes', 'firstYearVotesPct',
    'secondYearVotes', 'secondYearVotesPct',
    'thirdYearVotes', 'thirdYearVotesPct',
    'abstainVotes', 'voteWithheld', 'brokerNonVote', 'totalVotes',
    'voteResult', 'decidedBy', 'delete'
  ];

  // Forms
  ordinaryForm: FormGroup = this.fb.group({
    id: [null],
    meetingDate: [''],
    meetingType: [''],
    disclosureStatus: ['3'],
    capitalRepresentedShares: [null],
    capitalRepresentedSharesPct: [null],
    votesRepresentedShares: [null],
    votesRepresentedSharesPct: [null]
  });

  specialForm: FormGroup = this.fb.group({
    id: [null],
    meetingDate: [''],
    meetingType: [''],
    disclosureStatus: ['3'],
    capitalRepresentedShares: [null],
    capitalRepresentedSharesPct: [null],
    votesRepresentedShares: [null],
    votesRepresentedSharesPct: [null]
  });

  issueRowsForm: FormGroup = this.fb.group({
    rows: this.fb.array([])
  });

  sayOnPayRowsForm: FormGroup = this.fb.group({
    rows: this.fb.array([])
  });

  commentsForm: FormGroup = this.fb.group({
    comments: ['']
  });

  keywordsForm: FormGroup = this.fb.group({
    votingResults: ['']
  });

  // Track display names for proposal names (keyed by row index + table type)
  proposalDisplayNames: Map<string, string> = new Map();

  private deletedIssueIds: string[] = [];
  private deletedSayOnPayIds: string[] = [];

  get issueRows(): FormArray {
    return this.issueRowsForm.get('rows') as FormArray;
  }

  get sayOnPayRows(): FormArray {
    return this.sayOnPayRowsForm.get('rows') as FormArray;
  }

  get hasChanges(): boolean {
    return this.ordinaryForm.dirty || this.specialForm.dirty ||
           this.issueRowsForm.dirty || this.sayOnPayRowsForm.dirty ||
           this.commentsForm.dirty || this.keywordsForm.dirty ||
           this.deletedIssueIds.length > 0 || this.deletedSayOnPayIds.length > 0;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('votingResults'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'votingResults', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'votingResults', year: this.selectedYear }));
    }

    this.patchKeywords();
    this.loadLookupData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['keywords'] && !changes['keywords'].firstChange) {
      this.patchKeywords();
    }
  }

  private patchKeywords(): void {
    const kw = this.keywords.find(k => k.type === KeywordType.VotingResults);
    this.keywordsForm.patchValue({ votingResults: kw?.value ?? '' }, { emitEvent: false });
    this.keywordsForm.markAsPristine();
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch', 'Stay', 'swap_horiz', 'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  private getSaveDescription(): string {
    const hasKeywordChanges = this.keywordsForm.dirty;
    const hasVotingChanges = this.ordinaryForm.dirty || this.specialForm.dirty ||
      this.issueRowsForm.dirty || this.sayOnPayRowsForm.dirty || this.commentsForm.dirty ||
      this.deletedIssueIds.length > 0 || this.deletedSayOnPayIds.length > 0;
    if (hasVotingChanges && hasKeywordChanges) return 'voting results data and keywords';
    if (hasKeywordChanges) return 'keywords';
    return 'voting results data';
  }

  onSave(): void {
    if (!this.companyId) return;

    if (this.issueRows.invalid || this.sayOnPayRows.invalid) {
      this.issueRows.markAllAsTouched();
      this.sayOnPayRows.markAllAsTouched();
      this.toast.error('Each Issue and Say on Pay row must have an issue, category and subcategory before saving.');
      return;
    }

    this.performSave();
  }

  onDeleteIssue(row: CompanyVoteResultDto, index: number): void {
    if (row.id) {
      this.deletedIssueIds.push(row.id);
    }
    this.cascadeDeleteIssueSubRowsForParent(index);
    this.issueDataSource.data = this.issueDataSource.data.filter((_, i) => i !== index);
    this.issueRows.removeAt(index);
    this.renumberRanks();
    this.issueRowsForm.markAsDirty();
  }

  onDeleteSayOnPay(row: CompanyVoteResultDto, index: number): void {
    if (row.id) {
      this.deletedSayOnPayIds.push(row.id);
    }
    this.sayOnPayDataSource.data = this.sayOnPayDataSource.data.filter((_, i) => i !== index);
    this.sayOnPayRows.removeAt(index);
    this.renumberRanks();
    this.sayOnPayRowsForm.markAsDirty();
  }

  toggleRowHighlight(row: any): void {
    row.highlighted = !row.highlighted;
  }

  get selectedIssueRows(): CompanyVoteResultDto[] {
    return this.issueDataSource.data.filter((row: any) => row.highlighted);
  }

  get selectedSayOnPayRows(): CompanyVoteResultDto[] {
    return this.sayOnPayDataSource.data.filter((row: any) => row.highlighted);
  }

  onDeleteSelectedIssues(): void {
    const selected = this.selectedIssueRows;
    if (selected.length === 0) return;

    this.confirmationDialog.openConfirmationDialog(
      'Delete Issues',
      `Are you sure you want to delete the selected ${selected.length} issue${selected.length === 1 ? '' : 's'}?`,
      'Delete', 'Cancel', 'delete', 'close'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      const parentIndices = selected
        .map(row => this.issueDataSource.data.indexOf(row))
        .filter(i => i !== -1 && !this.issueRows.at(i).value.isElectionSubRow);
      for (const pi of parentIndices) {
        this.cascadeDeleteIssueSubRowsForParent(pi);
      }
      this.removeSelectedRows(selected, this.issueDataSource, this.issueRows, this.deletedIssueIds);
      this.renumberRanks();
      this.issueRowsForm.markAsDirty();
    });
  }

  onDeleteSelectedSayOnPay(): void {
    const selected = this.selectedSayOnPayRows;
    if (selected.length === 0) return;

    this.confirmationDialog.openConfirmationDialog(
      'Delete Say on Pay',
      `Are you sure you want to delete the selected ${selected.length} row${selected.length === 1 ? '' : 's'}?`,
      'Delete', 'Cancel', 'delete', 'close'
    ).subscribe(confirmed => {
      if (!confirmed) return;
      this.removeSelectedRows(selected, this.sayOnPayDataSource, this.sayOnPayRows, this.deletedSayOnPayIds);
      this.renumberRanks();
      this.sayOnPayRowsForm.markAsDirty();
    });
  }

  private removeSelectedRows(
    selected: CompanyVoteResultDto[],
    dataSource: MatTableDataSource<CompanyVoteResultDto>,
    formArray: FormArray,
    deletedIds: string[]
  ): void {
    const indices = selected
      .map(row => dataSource.data.indexOf(row))
      .filter(i => i !== -1)
      .sort((a, b) => b - a);

    for (const i of indices) {
      const row = dataSource.data[i];
      if (row.id) deletedIds.push(row.id);
    }
    const indexSet = new Set(indices);
    dataSource.data = dataSource.data.filter((_, i) => !indexSet.has(i));
    for (const i of indices) {
      formArray.removeAt(i);
    }
  }

  onAddIssue(): void {
    const newRow = this.createEmptyVoteResult(false);
    newRow.voteType = 1;
    newRow.voteRank = this.getNextRank(1);
    this.issueDataSource.data = [...this.issueDataSource.data, newRow];
    const newGroup = this.createVoteResultRowGroup(newRow);
    this.issueRows.push(newGroup);
    newGroup.get('isShareholderProposal')!.setValue('false');
    newGroup.markAsDirty();
    this.issueRowsForm.markAsDirty();
  }

  onAddSayOnPay(): void {
    const newRow = this.createEmptyVoteResult(true);
    newRow.voteType = 1;
    newRow.voteRank = this.getNextRank(1);

    const compensationCat = this.parentCategories.find(c => c.name === 'Compensation');
    if (compensationCat) {
      newRow.categoryId = compensationCat.id;
      const sayOnPaySubcat = this.subcategories.find(
        s => s.name === 'Say on Pay' && s.parentId === compensationCat.id
      );
      if (sayOnPaySubcat) {
        newRow.subcategoryId = sayOnPaySubcat.id;
      }
    }

    this.sayOnPayDataSource.data = [...this.sayOnPayDataSource.data, newRow];
    const newGroup = this.createVoteResultRowGroup(newRow);
    this.sayOnPayRows.push(newGroup);
    newGroup.get('isShareholderProposal')!.setValue('false');
    newGroup.markAsDirty();
    this.sayOnPayRowsForm.markAsDirty();
  }

  /**
   * Returns the next available rank for a new row of the given voteType,
   * counting non-sub rows across BOTH the Issues and Say on Pay tables.
   * Election sub-rows are excluded — they inherit their parent's rank.
   */
  private getNextRank(voteType: number, excludeGroup?: AbstractControl): number {
    let maxRank = 0;
    for (let i = 0; i < this.issueRows.length; i++) {
      const row = this.issueRows.at(i);
      if (row === excludeGroup) continue;
      if (row.value.isElectionSubRow) continue;
      const rowType = row.value.voteType ? parseInt(row.value.voteType) : null;
      if (rowType !== voteType) continue;
      const rank = row.value.voteRank;
      if (rank != null && rank > maxRank) maxRank = rank;
    }
    for (let i = 0; i < this.sayOnPayRows.length; i++) {
      const row = this.sayOnPayRows.at(i);
      if (row === excludeGroup) continue;
      const rowType = row.value.voteType ? parseInt(row.value.voteType) : null;
      if (rowType !== voteType) continue;
      const rank = row.value.voteRank;
      if (rank != null && rank > maxRank) maxRank = rank;
    }
    return maxRank + 1;
  }

  /**
   * Renumbers non-sub rows of both voteTypes contiguously (1, 2, 3...) across
   * Issues + Say on Pay. Order is preserved by sorting on the existing voteRank
   * (insertion order across tables), so reloading after save or compacting after
   * delete never reshuffles rows the user already placed. Rows with no rank yet
   * go last in their original encounter order.
   * Election sub-rows track their parent: when a parent's rank changes, sub-rows
   * matching the old rank are updated to the new rank in the same pass.
   */
  private renumberRanks(): void {
    for (const voteType of [1, 2]) {
      const refs: { group: AbstractControl; oldRank: number | null; seq: number }[] = [];
      let seq = 0;

      for (let i = 0; i < this.issueRows.length; i++) {
        const r = this.issueRows.at(i);
        const rType = r.value.voteType ? parseInt(r.value.voteType) : null;
        if (rType !== voteType) continue;
        if (r.value.isElectionSubRow) continue;
        refs.push({ group: r, oldRank: r.value.voteRank, seq: seq++ });
      }
      for (let i = 0; i < this.sayOnPayRows.length; i++) {
        const r = this.sayOnPayRows.at(i);
        const rType = r.value.voteType ? parseInt(r.value.voteType) : null;
        if (rType !== voteType) continue;
        refs.push({ group: r, oldRank: r.value.voteRank, seq: seq++ });
      }

      refs.sort((a, b) => {
        const aHas = a.oldRank != null;
        const bHas = b.oldRank != null;
        if (aHas && bHas) {
          if (a.oldRank! !== b.oldRank!) return a.oldRank! - b.oldRank!;
          return a.seq - b.seq;
        }
        if (aHas) return -1;
        if (bHas) return 1;
        return a.seq - b.seq;
      });

      refs.forEach((ref, idx) => {
        const newRank = idx + 1;
        if (ref.oldRank === newRank) return;
        ref.group.get('voteRank')!.setValue(newRank, { emitEvent: false });
        if (ref.oldRank == null) return;
        for (let j = 0; j < this.issueRows.length; j++) {
          const sub = this.issueRows.at(j);
          if (!sub.value.isElectionSubRow) continue;
          const subType = sub.value.voteType ? parseInt(sub.value.voteType) : null;
          if (subType !== voteType) continue;
          if (sub.value.voteRank === ref.oldRank) {
            sub.get('voteRank')!.setValue(newRank, { emitEvent: false });
          }
        }
      });
    }
  }

  /**
   * Removes any election sub-rows linked to the parent at parentIndex (same voteRank,
   * same voteType). Called before deleting an election parent so sub-rows don't
   * orphan and visually re-attach to another row after renumbering.
   */
  private cascadeDeleteIssueSubRowsForParent(parentIndex: number): void {
    const parent = this.issueRows.at(parentIndex);
    if (!parent) return;
    if (parent.value.isElectionSubRow) return;
    const parentRank = parent.value.voteRank;
    if (parentRank == null) return;
    const parentType = parent.value.voteType ? parseInt(parent.value.voteType) : null;

    const subIndices: number[] = [];
    for (let i = 0; i < this.issueRows.length; i++) {
      if (i === parentIndex) continue;
      const r = this.issueRows.at(i);
      if (!r.value.isElectionSubRow) continue;
      if (r.value.voteRank !== parentRank) continue;
      const rType = r.value.voteType ? parseInt(r.value.voteType) : null;
      if (rType !== parentType) continue;
      subIndices.push(i);
    }

    subIndices.sort((a, b) => b - a);
    for (const idx of subIndices) {
      const rowData = this.issueDataSource.data[idx];
      if (rowData?.id) this.deletedIssueIds.push(rowData.id);
      this.issueDataSource.data = this.issueDataSource.data.filter((_, i) => i !== idx);
      this.issueRows.removeAt(idx);
    }
  }

  onOpenProposalNameDialog(tableType: 'issue' | 'sayOnPay', index: number): void {
    const rows = tableType === 'issue' ? this.issueRows : this.sayOnPayRows;
    const row = rows.at(index);
    if (!row) return;

    // Only allow if shareholder proposal is "Yes"
    if (row.value.isShareholderProposal !== 'true') {
      this.toast.warning('Proposal Name can only be set when Sh. Prop. is "Yes"');
      return;
    }

    const dialogRef = this.dialog.open(AddProposalNameDialogComponent, {
      width: '900px',
      panelClass: 'connections-dialog-panel',
      data: { year: this.selectedYear }
    });

    dialogRef.afterClosed().subscribe((result: AddProposalNameDialogResult | null) => {
      if (!result) return;

      const item = result.item;
      const key = `${tableType}-${index}`;

      // Set the display name
      this.proposalDisplayNames.set(key, item.name);

      // Map entity type to the correct ID field
      switch (item.entityType) {
        case EntityType.Director:
          row.patchValue({
            proposalNameId: null,
            proposalDirectorId: item.id,
            proposalLargeShareholderId: null
          });
          break;
        case EntityType.LargeShareholder:
          row.patchValue({
            proposalNameId: null,
            proposalDirectorId: null,
            proposalLargeShareholderId: item.id
          });
          break;
        case EntityType.VoteProposalName:
          row.patchValue({
            proposalNameId: item.id,
            proposalDirectorId: null,
            proposalLargeShareholderId: null
          });
          break;
      }

      row.markAsDirty();
      if (tableType === 'issue') {
        this.issueRowsForm.markAsDirty();
      } else {
        this.sayOnPayRowsForm.markAsDirty();
      }
    });
  }

  onClearProposalName(tableType: 'issue' | 'sayOnPay', index: number): void {
    const rows = tableType === 'issue' ? this.issueRows : this.sayOnPayRows;
    const row = rows.at(index);
    if (!row) return;

    row.patchValue({
      proposalNameId: null,
      proposalDirectorId: null,
      proposalLargeShareholderId: null
    });

    this.proposalDisplayNames.delete(`${tableType}-${index}`);
    row.markAsDirty();
    if (tableType === 'issue') {
      this.issueRowsForm.markAsDirty();
    } else {
      this.sayOnPayRowsForm.markAsDirty();
    }
  }

  getProposalDisplayName(tableType: 'issue' | 'sayOnPay', index: number, row: CompanyVoteResultDto): string {
    const key = `${tableType}-${index}`;
    if (this.proposalDisplayNames.has(key)) {
      return this.proposalDisplayNames.get(key)!;
    }
    // Fallback: show name from DTO if available
    if (row.proposalDirectorId && row.proposalDirectorName) {
      return row.proposalDirectorName;
    }
    if (row.proposalLargeShareholderId && row.proposalLargeShareholderName) {
      return row.proposalLargeShareholderName;
    }
    if (row.proposalNameId && row.proposalNameFullName) {
      return row.proposalNameFullName;
    }
    return '';
  }

  hasProposalName(tableType: 'issue' | 'sayOnPay', index: number): boolean {
    const rows = tableType === 'issue' ? this.issueRows : this.sayOnPayRows;
    const row = rows.at(index);
    if (!row) return false;
    return !!(row.value.proposalNameId || row.value.proposalDirectorId || row.value.proposalLargeShareholderId);
  }

  isShareholderProposal(tableType: 'issue' | 'sayOnPay', index: number): boolean {
    const rows = tableType === 'issue' ? this.issueRows : this.sayOnPayRows;
    const row = rows.at(index);
    return row?.value.isShareholderProposal === 'true';
  }

  /**
   * Returns true if this is the parent election row (Board of Directors + Election/Board + forVotes = 0).
   */
  isElectionParentRow(index: number): boolean {
    const row = this.issueRows.at(index);
    if (!row) return false;
    const val = row.value;
    if (!this.isBoardElectionRow(val.categoryId, val.subcategoryId)) return false;
    const displayRank = this.getDisplayRank(index);
    return !displayRank.includes('.');
  }

  onOpenElectionDialog(parentIndex: number): void {
    if (!this.companyId) return;

    const parentRow = this.issueRows.at(parentIndex);
    if (!parentRow) return;

    const parentRank = parentRow.value.voteRank;

    // Collect existing election sub-row board member IDs
    const existingBoardMemberIds: string[] = [];
    for (let i = 0; i < this.issueRows.length; i++) {
      if (i === parentIndex) continue;
      const r = this.issueRows.at(i);
      if (r.value.voteRank === parentRank &&
          this.isBoardElectionRow(r.value.categoryId, r.value.subcategoryId)) {
        if (r.value.boardMemberId) {
          existingBoardMemberIds.push(r.value.boardMemberId);
        }
      }
    }

    const dialogRef = this.dialog.open(ElectionDialogComponent, {
      width: '600px',
      panelClass: 'connections-dialog-panel',
      data: {
        companyId: this.companyId,
        year: this.selectedYear,
        existingBoardMemberIds
      }
    });

    dialogRef.afterClosed().subscribe((result: ElectionDialogResult | null) => {
      if (!result) return;

      const parentCategoryId = parentRow.value.categoryId;
      const parentSubcategoryId = parentRow.value.subcategoryId;

      // Find insertion point: after the last sub-row for this parent rank
      let insertIndex = parentIndex + 1;
      for (let i = parentIndex + 1; i < this.issueRows.length; i++) {
        const r = this.issueRows.at(i);
        if (r.value.voteRank === parentRank &&
            this.isBoardElectionRow(r.value.categoryId, r.value.subcategoryId)) {
          insertIndex = i + 1;
        } else {
          break;
        }
      }

      // Build new sub-rows sorted alphabetically
      const selectedCandidates = result.candidates
        .filter(c => c.selected)
        .sort((a, b) => a.name.localeCompare(b.name));

      const newRows: CompanyVoteResultDto[] = [];
      const newGroups: FormGroup[] = [];

      for (const candidate of selectedCandidates) {
        const newRow = this.createEmptyVoteResult(false);
        newRow.voteRank = parentRank;
        newRow.categoryId = parentCategoryId;
        newRow.categoryName = 'Board of Directors';
        newRow.subcategoryId = parentSubcategoryId;
        newRow.subcategoryName = 'Election/Board';
        newRow.issue = `Election of ${candidate.name}`;
        newRow.voteType = parentRow.value.voteType ? parseInt(parentRow.value.voteType) : null;
        newRow.isElectionSubRow = true;
        if (candidate.boardMemberId) {
          newRow.boardMemberId = candidate.boardMemberId;
        }

        const newGroup = this.createVoteResultRowGroup(newRow);
        newGroup.get('isShareholderProposal')!.setValue('false');
        newGroup.markAsDirty();
        newRows.push(newRow);
        newGroups.push(newGroup);
      }

      // Insert at the correct position
      const dataArr = [...this.issueDataSource.data];
      dataArr.splice(insertIndex, 0, ...newRows);
      this.issueDataSource.data = dataArr;

      for (let j = 0; j < newGroups.length; j++) {
        this.issueRows.insert(insertIndex + j, newGroups[j]);
      }

      this.issueRowsForm.markAsDirty();
    });
  }

  private removeElectionSubRows(parentIndex: number, parentRank: number, categoryId: string, subcategoryId: string): void {
    // Find indices to remove (in reverse to avoid index shift issues)
    const indicesToRemove: number[] = [];
    for (let i = 0; i < this.issueRows.length; i++) {
      if (i === parentIndex) continue;
      const r = this.issueRows.at(i);
      if (r.value.voteRank === parentRank &&
          r.value.categoryId === categoryId &&
          r.value.subcategoryId === subcategoryId &&
          (r.value.forVotes == null || r.value.forVotes === 0 || r.value.forVotes > 0)) {
        // Only remove sub-rows (rows with forVotes > 0 or that are not the parent)
        if (r.value.forVotes == null || r.value.forVotes > 0) {
          // Track for deletion if they have an ID
          const rowData = this.issueDataSource.data[i];
          if (rowData?.id) {
            this.deletedIssueIds.push(rowData.id);
          }
          indicesToRemove.push(i);
        }
      }
    }

    // Remove in reverse order
    for (const idx of indicesToRemove.sort((a, b) => b - a)) {
      this.issueDataSource.data = this.issueDataSource.data.filter((_, i) => i !== idx);
      this.issueRows.removeAt(idx);
    }
  }

  /**
   * Returns the display rank for a row in the issue table.
   * Non-sub rows show their persisted voteRank directly (assigned at insert time and
   * compacted gap-free on delete). Election sub-rows show `{parentRank}.{n}` where
   * `n` is the alphabetical position among siblings sharing the parent's voteRank.
   */
  getDisplayRank(index: number): string {
    const row = this.issueRows.at(index);
    if (!row) return '';

    const voteRank = row.value.voteRank;
    if (voteRank == null) return '';

    if (!row.value.isElectionSubRow) {
      return voteRank.toString();
    }

    const voteType = row.value.voteType ? parseInt(row.value.voteType) : 1;
    const subs: { index: number; issue: string }[] = [];
    for (let i = 0; i < this.issueRows.length; i++) {
      const r = this.issueRows.at(i);
      if (!r.value.isElectionSubRow) continue;
      if (r.value.voteRank !== voteRank) continue;
      const rType = r.value.voteType ? parseInt(r.value.voteType) : 1;
      if (rType !== voteType) continue;
      subs.push({ index: i, issue: (r.value.issue || '').toLowerCase() });
    }
    subs.sort((a, b) => a.issue.localeCompare(b.issue));
    const subIdx = subs.findIndex(s => s.index === index);
    return subIdx >= 0 ? `${voteRank}.${subIdx + 1}` : voteRank.toString();
  }

  /**
   * Returns the display rank for a row in the Say on Pay table — its persisted
   * voteRank. Ranks are shared with the Issue table per voteType, so the two
   * tables never collide and adding/removing rows in one doesn't shift the other.
   */
  getSayOnPayDisplayRank(index: number): string {
    const row = this.sayOnPayRows.at(index);
    if (!row) return '';
    const voteRank = row.value.voteRank;
    return voteRank != null ? voteRank.toString() : '';
  }

  /**
   * Sorts issue rows by voteRank.
   * For "Board of Directors" + "Election/Board" rows sharing the same rank:
   *   - Parent row (no boardMemberId) comes first
   *   - Sub-rows (have boardMemberId) are sorted alphabetically by issue name
   */
  private sortIssueRows(rows: CompanyVoteResultDto[]): CompanyVoteResultDto[] {
    return rows.sort((a, b) => {
      // Group by vote type first (O before S)
      const typeA = a.voteType ?? 1;
      const typeB = b.voteType ?? 1;
      if (typeA !== typeB) return typeA - typeB;

      const rankA = a.voteRank ?? 0;
      const rankB = b.voteRank ?? 0;

      // Different ranks — sort by rank
      if (rankA !== rankB) return rankA - rankB;

      // Same rank — check if they're election rows
      const aIsElection = this.isBoardElectionRowFromDto(a);
      const bIsElection = this.isBoardElectionRowFromDto(b);

      // Non-election rows with same rank: keep original order
      if (!aIsElection && !bIsElection) return 0;

      // Election vs non-election at same rank: election comes first
      if (aIsElection && !bIsElection) return -1;
      if (!aIsElection && bIsElection) return 1;

      // Both election rows at same rank:
      // Parent (isElectionSubRow = false) comes first
      const aIsParent = !a.isElectionSubRow;
      const bIsParent = !b.isElectionSubRow;
      if (aIsParent && !bIsParent) return -1;
      if (!aIsParent && bIsParent) return 1;

      // Both sub-rows: sort alphabetically by issue
      return (a.issue ?? '').localeCompare(b.issue ?? '');
    });
  }

  /**
   * Checks if a DTO row is a "Board of Directors" + "Election/Board" row using category names from the DTO.
   */
  private isBoardElectionRowFromDto(row: CompanyVoteResultDto): boolean {
    if (!row.categoryId || !row.subcategoryId) return false;
    const category = this.categories.find(c => c.id === row.categoryId);
    const subcategory = this.categories.find(c => c.id === row.subcategoryId);
    if (!category || !subcategory) return false;
    return category.name === 'Board of Directors' && subcategory.name === 'Election/Board';
  }

  /**
   * Checks if a row is a "Board of Directors" + "Election/Board" row by category/subcategory names.
   */
  isBoardElectionRow(categoryId: string | null, subcategoryId: string | null): boolean {
    if (!categoryId || !subcategoryId) return false;

    const category = this.categories.find(c => c.id === categoryId);
    const subcategory = this.categories.find(c => c.id === subcategoryId);

    if (!category || !subcategory) return false;

    return category.name === 'Board of Directors' && subcategory.name === 'Election/Board';
  }

  private createEmptyVoteResult(isSayOnPay: boolean): CompanyVoteResultDto {
    return {
      id: '',
      companyMeetingId: this.meetingId ?? '',
      voteType: null,
      voteRank: null,
      issue: null,
      categoryId: null,
      categoryName: null,
      subcategoryId: null,
      subcategoryName: null,
      requirementId: null,
      requirementName: null,
      isShareholderProposal: null,
      boardVoteRecommendationId: null,
      boardVoteRecommendationName: null,
      boardMemberId: null,
      isElectionSubRow: null,
      proposalDirectorId: null,
      proposalDirectorName: null,
      proposalLargeShareholderId: null,
      proposalLargeShareholderName: null,
      proposalNameId: null,
      proposalNameFullName: null,
      isSayOnPay,
      forVotes: null,
      forVotesPct: null,
      againstVotes: null,
      againstVotesPct: null,
      firstYearVotes: null,
      firstYearVotesPct: null,
      secondYearVotes: null,
      secondYearVotesPct: null,
      thirdYearVotes: null,
      thirdYearVotesPct: null,
      abstainVotes: null,
      voteWithheld: null,
      brokerNonVote: null,
      totalVotes: null,
      voteResultId: null,
      voteResultName: null,
      decidedById: null,
      decidedByName: null,
      createdDate: '',
      createdBy: '',
      updatedDate: '',
      updatedBy: ''
    };
  }

  getSubcategoriesForRow(categoryId: string | null): VoteResultsCategory[] {
    if (!categoryId) return [];
    return this.subcategories.filter(s => s.parentId === categoryId);
  }

  getCategoryName(categoryId: string | null): string {
    if (!categoryId) return '';
    return this.parentCategories.find(c => c.id === categoryId)?.name ?? '';
  }

  getSubcategoryName(subcategoryId: string | null): string {
    if (!subcategoryId) return '';
    return this.subcategories.find(s => s.id === subcategoryId)?.name ?? '';
  }

  getOptionName(options: VoteResultsOption[], id: string | null): string {
    if (!id) return '';
    return options.find(o => o.id === id)?.name ?? '';
  }

  getVoteTypeLabel(value: string | null): string {
    if (value === '1') return 'O';
    if (value === '2') return 'S';
    return '';
  }

  getShareholderProposalLabel(value: string | null): string {
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';
    return '';
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9/]/g, '');
    if (value.length === 2 && !value.includes('/')) {
      value += '/';
    } else if (value.length === 5 && value.indexOf('/', 3) === -1) {
      value += '/';
    }
    input.value = value;
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'votingResults', year }));
    this.loadData();
  }

  private loadLookupData(): void {
    if (!this.companyId) return;

    this.spinnerService.show();

    forkJoin({
      options: this.lutService.getVoteResultsOptions(),
      categories: this.lutService.getVoteResultsCategories()
    }).subscribe({
      next: ({ options, categories }) => {
        this.allOptions = options;
        this.boardVoteRecOptions = options.filter(o => o.type === 1).sort((a, b) => a.name.localeCompare(b.name));
        this.decidedByOptions = options.filter(o => o.type === 2).sort((a, b) => a.name.localeCompare(b.name));
        this.voteRequirementOptions = options.filter(o => o.type === 6).sort((a, b) => a.name.localeCompare(b.name));
        this.voteResultOptions = options.filter(o => o.type === 7).sort((a, b) => a.name.localeCompare(b.name));

        this.categories = categories;
        this.parentCategories = categories.filter(c => !c.parentId).sort((a, b) => a.name.localeCompare(b.name));
        this.subcategories = categories.filter(c => !!c.parentId).sort((a, b) => a.name.localeCompare(b.name));

        this.loadData();
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error('Failed to load lookup data');
        console.error('Lookup data error:', err);
      }
    });
  }

  private loadData(): void {
    if (!this.companyId) {
      this.spinnerService.hide();
      return;
    }

    this.deletedIssueIds = [];
    this.deletedSayOnPayIds = [];
    this.spinnerService.show();

    this.meetingService.getMeeting(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        const formValues = resource.state === 'present'
          ? resource.data
          : (resource.state === 'carried-forward' ? resource.carriedForward?.values : null);
        this.showYearWarning = resource.state === 'carried-forward' || resource.state === 'absent';
        if (formValues) {
          this.meetingId = formValues.companyMeetingId;
          this.populateData(formValues);
        } else {
          this.resetData();
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.resetData();
        } else {
          this.toast.error('Error loading voting results data');
        }
        console.error('Voting results data error:', error);
      }
    });
  }

  private populateData(response: CompanyMeetingResponseDto): void {
    this.meetingId = response.companyMeetingId;
    this.proposalDisplayNames.clear();

    // Populate meeting details
    const ordinaryDetail = response.meetingDetails.find(d => d.meetingType === 1);
    const specialDetail = response.meetingDetails.find(d => d.meetingType === 2);

    this.populateMeetingForm(this.ordinaryForm, ordinaryDetail, '1');
    this.populateMeetingForm(this.specialForm, specialDetail, '2');

    // Populate vote results (sorted by voteRank, with election sub-rows sorted alphabetically)
    const issues = this.sortIssueRows(response.voteResults.filter(v => !v.isSayOnPay));
    const sayOnPay = response.voteResults.filter(v => v.isSayOnPay);

    this.issueDataSource.data = issues;
    this.issueRowsForm.setControl('rows', this.fb.array(
      issues.map(r => this.createVoteResultRowGroup(r))
    ));

    this.sayOnPayDataSource.data = sayOnPay;
    this.sayOnPayRowsForm.setControl('rows', this.fb.array(
      sayOnPay.map(r => this.createVoteResultRowGroup(r))
    ));

    // Comments
    this.commentsForm.patchValue({ comments: response.comments ?? '' });

    // Legacy data may have per-table or per-voteType numbering inconsistencies;
    // normalize to the unified cross-table sequence on load.
    this.renumberRanks();
    this.markAllPristine();
  }

  private populateMeetingForm(form: FormGroup, detail: CompanyMeetingDetailDto | undefined, defaultMeetingType: string): void {
    if (detail) {
      form.patchValue({
        id: detail.id,
        meetingDate: this.formatDate(detail.meetingDate),
        meetingType: detail.meetingType?.toString() ?? defaultMeetingType,
        disclosureStatus: detail.disclosureStatus?.toString() ?? '3',
        capitalRepresentedShares: detail.capitalRepresentedShares,
        capitalRepresentedSharesPct: detail.capitalRepresentedSharesPct,
        votesRepresentedShares: detail.votesRepresentedShares,
        votesRepresentedSharesPct: detail.votesRepresentedSharesPct
      });
    } else {
      form.reset({ id: null, meetingDate: '', meetingType: defaultMeetingType, disclosureStatus: '3' });
    }
  }

  private createVoteResultRowGroup(row: CompanyVoteResultDto): FormGroup {
    const group = this.fb.group({
      id: [row.id],
      voteType: [row.voteType?.toString() ?? ''],
      voteRank: [row.voteRank],
      issue: [row.issue ?? '', Validators.required],
      categoryId: [row.categoryId ?? '', Validators.required],
      subcategoryId: [row.subcategoryId ?? '', Validators.required],
      requirementId: [row.requirementId ?? ''],
      isShareholderProposal: [row.isShareholderProposal != null ? (row.isShareholderProposal ? 'true' : 'false') : ''],
      proposalNameId: [row.proposalNameId ?? ''],
      proposalDirectorId: [row.proposalDirectorId ?? ''],
      proposalLargeShareholderId: [row.proposalLargeShareholderId ?? ''],
      boardMemberId: [row.boardMemberId ?? ''],
      isElectionSubRow: [!!row.isElectionSubRow],
      boardVoteRecommendationId: [row.boardVoteRecommendationId ?? ''],
      forVotes: [row.forVotes],
      forVotesPct: [row.forVotesPct],
      againstVotes: [row.againstVotes],
      againstVotesPct: [row.againstVotesPct],
      firstYearVotes: [row.firstYearVotes],
      firstYearVotesPct: [row.firstYearVotesPct],
      secondYearVotes: [row.secondYearVotes],
      secondYearVotesPct: [row.secondYearVotesPct],
      thirdYearVotes: [row.thirdYearVotes],
      thirdYearVotesPct: [row.thirdYearVotesPct],
      abstainVotes: [row.abstainVotes],
      voteWithheld: [row.voteWithheld],
      brokerNonVote: [row.brokerNonVote],
      totalVotes: [row.totalVotes],
      voteResultId: [row.voteResultId ?? ''],
      decidedById: [row.decidedById ?? '']
    });

    this.setupRowSubscriptions(group);
    return group;
  }

  /**
   * Sets up reactive subscriptions for a vote result row:
   * - For% ↔ Against% reciprocal calculation
   * - Total Votes auto-sum
   * - Shareholder Proposal toggle → defaults for Board Vote Rec and Vote Result
   * - Decided By propagation from first row
   */
  private setupRowSubscriptions(group: FormGroup): void {
    // #2: For% ↔ Against% reciprocal
    group.get('forVotesPct')!.valueChanges.subscribe(val => {
      if (val != null && val !== '') {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        if (!isNaN(num)) {
          const against = Math.round((100 - num) * 100) / 100;
          const current = group.get('againstVotesPct')!.value;
          if (current !== against) {
            group.get('againstVotesPct')!.setValue(against, { emitEvent: false });
          }
        }
      }
    });

    group.get('againstVotesPct')!.valueChanges.subscribe(val => {
      if (val != null && val !== '') {
        const num = typeof val === 'string' ? parseFloat(val) : val;
        if (!isNaN(num)) {
          const forPct = Math.round((100 - num) * 100) / 100;
          const current = group.get('forVotesPct')!.value;
          if (current !== forPct) {
            group.get('forVotesPct')!.setValue(forPct, { emitEvent: false });
          }
        }
      }
    });

    // #3: Total Votes auto-sum
    const voteFields = [
      'forVotes', 'againstVotes', 'abstainVotes', 'voteWithheld', 'brokerNonVote',
      'firstYearVotes', 'secondYearVotes', 'thirdYearVotes'
    ];
    for (const field of voteFields) {
      group.get(field)!.valueChanges.subscribe(() => {
        this.recalculateTotalVotes(group);
      });
    }

    // On Issue rows: when For has votes and Against is empty, derive For % from
    // For and Vote Withheld (falls back to 100% only if Withheld is also empty)
    group.get('forVotes')!.valueChanges.subscribe(() => this.maybeSetForPct100(group));
    group.get('againstVotes')!.valueChanges.subscribe(() => this.maybeSetForPct100(group));
    group.get('voteWithheld')!.valueChanges.subscribe(() => this.maybeSetForPct100(group));

    // #4 & #5: Shareholder Proposal toggle → set defaults (overrides existing values)
    group.get('isShareholderProposal')!.valueChanges.subscribe(val => {
      const isSayOnPay = this.sayOnPayRows.controls.includes(group);
      if (val === 'true') {
        // Shareholder proposal: Board Vote Rec = "Against", Vote Result = "Not passed"
        const againstOpt = this.boardVoteRecOptions.find(o => o.name === 'Against');
        const notPassedOpt = this.voteResultOptions.find(o => o.name === 'Not passed');
        if (againstOpt) {
          group.get('boardVoteRecommendationId')!.setValue(againstOpt.id);
        }
        if (notPassedOpt) {
          group.get('voteResultId')!.setValue(notPassedOpt.id);
        }
      } else if (val === 'false') {
        // Company proposal defaults differ between Issue and Say on Pay tables
        const recName = isSayOnPay ? '1 Year' : 'For';
        const resultName = isSayOnPay ? 'One year' : 'Pass';
        const recOpt = this.boardVoteRecOptions.find(o => o.name === recName);
        const resultOpt = this.voteResultOptions.find(o => o.name === resultName);
        if (recOpt) {
          group.get('boardVoteRecommendationId')!.setValue(recOpt.id);
        }
        if (resultOpt) {
          group.get('voteResultId')!.setValue(resultOpt.id);
        }
      }
    });

    // #6: Decided By propagation — when first row's decidedBy changes, propagate to subsequent rows
    group.get('decidedById')!.valueChanges.subscribe(val => {
      if (!val) return;
      this.propagateDecidedBy(val, group);
    });

    // Vote Type change → assign next available rank for the new voteType (across
    // both tables), then compact ranks so the OLD voteType has no gap.
    group.get('voteType')!.valueChanges.subscribe(val => {
      if (!val) return;
      if (group.value.isElectionSubRow) return;
      const typeNum = parseInt(val);
      const newRank = this.getNextRank(typeNum, group);
      if (group.value.voteRank !== newRank) {
        group.get('voteRank')!.setValue(newRank, { emitEvent: false });
        const isIssue = this.issueRows.controls.includes(group);
        const isSop = this.sayOnPayRows.controls.includes(group);
        if (isIssue) this.issueRowsForm.markAsDirty();
        if (isSop) this.sayOnPayRowsForm.markAsDirty();
      }
      this.renumberRanks();
    });
  }

  private maybeSetForPct100(group: FormGroup): void {
    if (this.sayOnPayRows.controls.includes(group)) return;
    const forVotes = group.get('forVotes')!.value;
    const againstVotes = group.get('againstVotes')!.value;
    const voteWithheld = group.get('voteWithheld')!.value;
    const hasForVotes = forVotes != null && forVotes !== '' && forVotes !== 0;
    const noAgainstVotes = againstVotes == null || againstVotes === '' || againstVotes === 0;
    if (!hasForVotes || !noAgainstVotes) return;

    const forNum = typeof forVotes === 'string' ? parseFloat(forVotes) : forVotes;
    const hasWithheld = voteWithheld != null && voteWithheld !== '' && voteWithheld !== 0;
    const withheldNum = hasWithheld
      ? (typeof voteWithheld === 'string' ? parseFloat(voteWithheld) : voteWithheld)
      : 0;

    const denom = forNum + withheldNum;
    const pct = denom > 0 ? Math.round((forNum / denom * 100) * 100) / 100 : 100;

    const current = group.get('forVotesPct')!.value;
    if (current !== pct) {
      group.get('forVotesPct')!.setValue(pct);
    }
  }

  private recalculateTotalVotes(group: FormGroup): void {
    const abstainV = group.get('abstainVotes')!.value || 0;
    const withheldV = group.get('voteWithheld')!.value || 0;
    const brokerV = group.get('brokerNonVote')!.value || 0;
    const baseTotal = abstainV + withheldV + brokerV;

    const isSayOnPay = this.sayOnPayRows.controls.includes(group);
    let total: number;
    if (isSayOnPay) {
      const y1 = group.get('firstYearVotes')!.value || 0;
      const y2 = group.get('secondYearVotes')!.value || 0;
      const y3 = group.get('thirdYearVotes')!.value || 0;
      total = y1 + y2 + y3 + baseTotal;
    } else {
      const forV = group.get('forVotes')!.value || 0;
      const againstV = group.get('againstVotes')!.value || 0;
      total = forV + againstV + baseTotal;
    }
    group.get('totalVotes')!.setValue(total || null, { emitEvent: false });
  }

  /**
   * When decidedBy is set on the first row, propagate to all subsequent rows.
   * For the issue table, the value overwrites existing values on every other row.
   * For the say-on-pay table, only empty rows are filled.
   */
  private propagateDecidedBy(decidedById: string, sourceGroup: AbstractControl): void {
    const issueFirst = this.issueRows.at(0);
    const sayOnPayFirst = this.sayOnPayRows.at(0);

    if (issueFirst && sourceGroup === issueFirst) {
      // Overwrite decidedBy on every other issue row
      for (let i = 1; i < this.issueRows.length; i++) {
        const row = this.issueRows.at(i);
        if (row.value.decidedById !== decidedById) {
          row.get('decidedById')!.setValue(decidedById, { emitEvent: false });
        }
      }
      this.issueRowsForm.markAsDirty();
      // Also fill empty say-on-pay rows
      for (let i = 0; i < this.sayOnPayRows.length; i++) {
        const row = this.sayOnPayRows.at(i);
        if (!row.value.decidedById) {
          row.get('decidedById')!.setValue(decidedById, { emitEvent: false });
        }
      }
    }

    if (sayOnPayFirst && sourceGroup === sayOnPayFirst) {
      for (let i = 1; i < this.sayOnPayRows.length; i++) {
        const row = this.sayOnPayRows.at(i);
        if (!row.value.decidedById) {
          row.get('decidedById')!.setValue(decidedById, { emitEvent: false });
        }
      }
    }
  }

  private resetData(): void {
    this.meetingId = null;
    this.proposalDisplayNames.clear();
    this.ordinaryForm.reset({ id: null, meetingDate: '', meetingType: '1', disclosureStatus: '3' });
    this.specialForm.reset({ id: null, meetingDate: '', meetingType: '2', disclosureStatus: '3' });
    this.issueDataSource.data = [];
    this.sayOnPayDataSource.data = [];
    this.issueRowsForm.setControl('rows', this.fb.array([]));
    this.sayOnPayRowsForm.setControl('rows', this.fb.array([]));
    this.commentsForm.patchValue({ comments: '' });
    this.markAllPristine();
  }

  private markAllPristine(): void {
    this.ordinaryForm.markAsPristine();
    this.specialForm.markAsPristine();
    this.issueRowsForm.markAsPristine();
    this.sayOnPayRowsForm.markAsPristine();
    this.commentsForm.markAsPristine();
    this.keywordsForm.markAsPristine();
  }

  private toIsoDate(displayDate: string): string | null {
    if (!displayDate) return null;
    if (/^\d{4}$/.test(displayDate)) return `${displayDate}-01-01T00:00:00Z`;
    const parts = displayDate.split('/');
    if (parts.length !== 3 || parts[2].length !== 4) return null;
    const [day, month, year] = parts;
    return `${year}-${month}-${day}T00:00:00Z`;
  }

  private performSave(): void {
    if (!this.companyId) return;

    const ordVal = this.ordinaryForm.value;
    const specVal = this.specialForm.value;

    const meetingDetails: CompanyMeetingDetailUpsertDto[] = [];

    // Always send ordinary detail (meetingType = 1)
    meetingDetails.push({
      id: ordVal.id || null,
      meetingDate: this.toIsoDate(ordVal.meetingDate),
      meetingType: 1,
      disclosureStatus: ordVal.disclosureStatus ? parseInt(ordVal.disclosureStatus) : null,
      capitalRepresentedShares: ordVal.capitalRepresentedShares,
      capitalRepresentedSharesPct: ordVal.capitalRepresentedSharesPct,
      votesRepresentedShares: ordVal.votesRepresentedShares,
      votesRepresentedSharesPct: ordVal.votesRepresentedSharesPct,
      isDeleted: false
    });

    // Always send special detail (meetingType = 2)
    meetingDetails.push({
      id: specVal.id || null,
      meetingDate: this.toIsoDate(specVal.meetingDate),
      meetingType: 2,
      disclosureStatus: specVal.disclosureStatus ? parseInt(specVal.disclosureStatus) : null,
      capitalRepresentedShares: specVal.capitalRepresentedShares,
      capitalRepresentedSharesPct: specVal.capitalRepresentedSharesPct,
      votesRepresentedShares: specVal.votesRepresentedShares,
      votesRepresentedSharesPct: specVal.votesRepresentedSharesPct,
      isDeleted: false
    });

    // Collect vote results
    const voteResults: CompanyVoteResultUpsertDto[] = [];

    // Issues
    this.issueRows.controls.forEach((group) => {
      const val = group.value;
      voteResults.push(this.buildVoteResultUpsert(val, false));
    });

    // Say on Pay
    this.sayOnPayRows.controls.forEach((group) => {
      const val = group.value;
      voteResults.push(this.buildVoteResultUpsert(val, true));
    });

    // Add deleted items
    for (const id of this.deletedIssueIds) {
      voteResults.push({ id, isDeleted: true } as CompanyVoteResultUpsertDto);
    }
    for (const id of this.deletedSayOnPayIds) {
      voteResults.push({ id, isDeleted: true, isSayOnPay: true } as CompanyVoteResultUpsertDto);
    }

    const request: CompanyMeetingUpsertRequest = {
      year: parseInt(this.selectedYear),
      comments: this.commentsForm.value.comments || null,
      meetingDetails,
      voteResults
    };

    const keywordRequests: CompanyKeywordUpdateRequest[] = buildKeywordRequests(
      this.keywordsForm.value,
      [{ formField: 'votingResults', type: KeywordType.VotingResults }],
      this.keywords
    );

    const hasVotingData = this.ordinaryForm.dirty || this.specialForm.dirty ||
      this.issueRowsForm.dirty || this.sayOnPayRowsForm.dirty || this.commentsForm.dirty ||
      this.deletedIssueIds.length > 0 || this.deletedSayOnPayIds.length > 0;

    const description = this.getSaveDescription();
    this.spinnerService.show();

    const saveCalls: Observable<any>[] = [];
    saveCalls.push(hasVotingData ? this.meetingService.updateMeeting(this.companyId!, request) : of(null));
    saveTabThenKeywords(saveCalls, this.companyKeywordService.upsert(this.companyId!, keywordRequests)).subscribe({
      next: ([_votingResult, keywordsResult]) => {
        this.deletedIssueIds = [];
        this.deletedSayOnPayIds = [];
        this.keywordsUpdated.emit(keywordsResult as CompanyKeywordDto[]);
        this.markAllPristine();
        this.spinnerService.hide();
        this.toast.success(`Saved ${description} successfully`);
        if (hasVotingData) {
          this.loadData();
        }
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toast.error(serverErrorMessage(err, `Failed to save ${description}`));
        console.error('Save error:', err);
      }
    });
  }

  private buildVoteResultUpsert(val: any, isSayOnPay: boolean): CompanyVoteResultUpsertDto {
    return {
      id: val.id || null,
      voteType: val.voteType ? parseInt(val.voteType) : null,
      voteRank: val.voteRank,
      issue: val.issue || null,
      categoryId: val.categoryId || null,
      subcategoryId: val.subcategoryId || null,
      requirementId: val.requirementId || null,
      isShareholderProposal: val.isShareholderProposal === 'true' ? true : val.isShareholderProposal === 'false' ? false : null,
      boardVoteRecommendationId: val.boardVoteRecommendationId || null,
      boardMemberId: val.boardMemberId || null,
      isElectionSubRow: val.isElectionSubRow || false,
      proposalDirectorId: val.proposalDirectorId || null,
      proposalLargeShareholderId: val.proposalLargeShareholderId || null,
      proposalNameId: val.proposalNameId || null,
      isSayOnPay,
      forVotes: val.forVotes,
      forVotesPct: val.forVotesPct,
      againstVotes: val.againstVotes,
      againstVotesPct: val.againstVotesPct,
      firstYearVotes: val.firstYearVotes,
      firstYearVotesPct: val.firstYearVotesPct,
      secondYearVotes: val.secondYearVotes,
      secondYearVotesPct: val.secondYearVotesPct,
      thirdYearVotes: val.thirdYearVotes,
      thirdYearVotesPct: val.thirdYearVotesPct,
      abstainVotes: val.abstainVotes,
      voteWithheld: val.voteWithheld,
      brokerNonVote: val.brokerNonVote,
      totalVotes: val.totalVotes,
      voteResultId: val.voteResultId || null,
      decidedById: val.decidedById || null,
      isDeleted: false
    };
  }
}
